import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Alert, Avatar, Button, CircularProgress, Typography } from "@mui/material";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Clock,
  FileText,
  FolderOpen,
  MessageSquare,
  RefreshCw,
  Ticket,
  User,
} from "lucide-react";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import "./TicketDetails.css";

const toTitleCase = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const TicketDetails = ({ isSidebarExpanded }) => {
  const { ticketId } = useParams();
  const location = useLocation();
  const [ticketData, setTicketData] = useState([]);
  const [ticketSummary, setTicketSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const ticketType = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get("type") || "").trim().toLowerCase();
  }, [location.search]);

  const parseTimestamp = (value) => {
    if (!value) return 0;
    const parsed = Date.parse(String(value));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const latestEntry =
    ticketData.length > 0
      ? [...ticketData].sort(
          (a, b) => parseTimestamp(b?.recorded_at_ist) - parseTimestamp(a?.recorded_at_ist),
        )[0]
      : null;

  const getFileViewUrl = (filePath = "") => {
    const normalizedPath = String(filePath).replace(/\\/g, "/").replace(/^\/+/, "");
    if (!normalizedPath) return "";
    const encodedPath = normalizedPath
      .split("/")
      .map((segment) => encodeURIComponent(segment))
      .join("/");
    return `${API_URL}/view_image/${encodedPath}`;
  };

  const handleOpenAttachment = useCallback(
    async (file) => {
      const fileUrl = getFileViewUrl(file?.file_path);
      if (!fileUrl) return;

      try {
        const response = await axiosInstance.get(fileUrl, {
          responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(response.data);
        window.open(objectUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch (err) {
        console.error("Failed to open attachment", err);
      }
    },
    [],
  );

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return { date: "N/A", time: "", full: "N/A" };
    }

    return {
      date: date.toLocaleDateString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      full: date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  const getInitials = (name) =>
    String(name || "")
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";

  const normalizeStatus = (status = "") =>
    String(status).trim().toLowerCase().replace(/[_\s]+/g, "_");

  const getStatusVariant = (status) => {
    const normalized = normalizeStatus(status);
    if (normalized === "resolved" || normalized === "closed") return "success";
    if (normalized === "in_progress" || normalized === "pending") return "warning";
    if (normalized === "open" || normalized === "new") return "info";
    return "default";
  };

  const getPriorityVariant = (priority) => {
    const normalized = String(priority || "").trim().toLowerCase();
    if (normalized === "critical" || normalized === "high") return "danger";
    if (normalized === "medium") return "warning";
    if (normalized === "low") return "info";
    return "default";
  };

  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    setError("");

    if (ticketType !== "flange" && ticketType !== "equipment") {
      setError("Ticket type is missing. Please open this screen from the Tickets page.");
      setTicketSummary(null);
      setTicketData([]);
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get(`/api/tickets/audit_log/${ticketId}`, {
        params: { type: ticketType },
      });
      setTicketSummary(response.data?.ticket || null);
      setTicketData(response.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch audit log", err);
      setError(err?.response?.data?.message || "Unable to load ticket activity log right now.");
      setTicketSummary(null);
      setTicketData([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId, ticketType]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const handleBack = () => {
    window.history.back();
  };

  const currentStatus = latestEntry?.to_status || ticketSummary?.status || "Unknown";
  const currentStatusTime = latestEntry?.recorded_at_ist || ticketSummary?.updated_at;
  const attachments = Array.isArray(ticketSummary?.files) ? ticketSummary.files : [];
  const statusVariant = getStatusVariant(currentStatus);
  const priorityVariant = getPriorityVariant(ticketSummary?.priority);

  return (
    <div
      className={`table-container ${isSidebarExpanded ? "expanded" : "collapsed"} ticket-details-page`}
    >
      <div className="ticket-details-shell">
        <div className="ticket-details-header-card">
          <button
            type="button"
            className="ticket-details-back-btn"
            onClick={handleBack}
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="ticket-details-header-copy">
            <div className="ticket-details-header-title-row">
              <span className="ticket-details-header-icon">
                <Ticket size={16} />
              </span>
              <Typography component="h1" className="ticket-details-header-title">
                Ticket Activity Log
              </Typography>
            </div>
            <Typography className="ticket-details-header-subtitle">
              {toTitleCase(ticketSummary?.ticket_type || ticketType || "ticket")} ticket #{ticketId}
            </Typography>
          </div>
        </div>

        {loading ? (
          <div className="ticket-details-state-card">
            <CircularProgress size={42} sx={{ color: "#d92d20" }} />
            <Typography className="ticket-details-state-title">
              Loading ticket history...
            </Typography>
          </div>
        ) : error ? (
          <div className="ticket-details-state-card">
            <Alert severity="error" className="ticket-details-error-alert">
              {error}
            </Alert>
            <Button
              onClick={fetchAuditLog}
              variant="contained"
              startIcon={<RefreshCw size={16} />}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                backgroundColor: "#d92d20",
                "&:hover": { backgroundColor: "#b42318" },
              }}
            >
              Retry
            </Button>
          </div>
        ) : (
          <>
            <section className="ticket-details-card">
              <div className="ticket-details-card-head">
                <div className="ticket-details-section-title">
                  <span className="ticket-details-section-icon">
                    <Activity size={16} />
                  </span>
                  <Typography className="ticket-details-section-heading">
                    Ticket Overview
                  </Typography>
                </div>
              </div>

              <div className="ticket-details-overview-grid">
                <div className="ticket-details-details-panel">
                  <div className="ticket-details-meta-grid">
                    <div className="ticket-details-meta-item">
                      <span className="ticket-details-meta-label">Raised By</span>
                      <span className="ticket-details-meta-value">
                        {ticketSummary?.raised_by || "Unknown User"}
                      </span>
                    </div>
                    <div className="ticket-details-meta-item">
                      <span className="ticket-details-meta-label">Project</span>
                      <span className="ticket-details-meta-value">
                        {ticketSummary?.project_name || "N/A"}
                      </span>
                    </div>
                    <div className="ticket-details-meta-item">
                      <span className="ticket-details-meta-label">Equipment</span>
                      <span className="ticket-details-meta-value">
                        {ticketSummary?.equipment_number || "N/A"}
                      </span>
                    </div>
                    <div className="ticket-details-meta-item">
                      <span className="ticket-details-meta-label">
                        {ticketType === "flange" ? "Flange" : "Department"}
                      </span>
                      <span className="ticket-details-meta-value">
                        {ticketType === "flange"
                          ? ticketSummary?.flange_number || "N/A"
                          : ticketSummary?.department_name || "N/A"}
                      </span>
                    </div>
                    <div className="ticket-details-meta-item">
                      <span className="ticket-details-meta-label">Priority</span>
                      <span
                        className={`ticket-details-badge ticket-details-badge-${priorityVariant}`}
                      >
                        {toTitleCase(ticketSummary?.priority || "N/A")}
                      </span>
                    </div>
                    <div className="ticket-details-meta-item">
                      <span className="ticket-details-meta-label">Created</span>
                      <span className="ticket-details-meta-value">
                        {formatDateTime(ticketSummary?.created_at).full}
                      </span>
                    </div>
                  </div>

                  <div className="ticket-details-remark-box">
                    <div className="ticket-details-remark-head">
                      <MessageSquare size={14} />
                      <span>Current Remark</span>
                    </div>
                    <Typography className="ticket-details-remark-text">
                      {ticketSummary?.remark || "No remark provided."}
                    </Typography>
                  </div>
                </div>

                <div className="ticket-details-status-panel">
                  <div className="ticket-details-status-label">Current Status</div>
                  <div className="ticket-details-status-row">
                    <span
                      className={`ticket-details-badge ticket-details-badge-${statusVariant}`}
                    >
                      {toTitleCase(currentStatus)}
                    </span>
                  </div>
                  <div className="ticket-details-status-time">
                    <Clock size={13} />
                    <span>As of {formatDateTime(currentStatusTime).full}</span>
                  </div>

                  <div className="ticket-details-mini-metrics">
                    <div className="ticket-details-mini-card">
                      <span className="ticket-details-mini-label">Type</span>
                      <span className="ticket-details-mini-value">
                        {toTitleCase(ticketSummary?.ticket_type || ticketType || "Ticket")}
                      </span>
                    </div>
                    <div className="ticket-details-mini-card">
                      <span className="ticket-details-mini-label">Actions</span>
                      <span className="ticket-details-mini-value">{ticketData.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="ticket-details-card">
              <div className="ticket-details-card-head">
                <div className="ticket-details-section-title">
                  <span className="ticket-details-section-icon">
                    <FolderOpen size={16} />
                  </span>
                  <Typography className="ticket-details-section-heading">
                    Attachments
                  </Typography>
                </div>
              </div>

              {attachments.length === 0 ? (
                <div className="ticket-details-empty-inline">No attachments uploaded</div>
              ) : (
                <div className="ticket-details-attachments-list">
                  {attachments.map((file) => {
                    return (
                      <button
                        type="button"
                        key={file.file_id || file.filename}
                        className="ticket-details-attachment-row"
                        onClick={() => handleOpenAttachment(file)}
                      >
                        <div className="ticket-details-attachment-file">
                          <span className="ticket-details-attachment-file-icon">
                            <FileText size={16} />
                          </span>
                          <div className="ticket-details-attachment-copy">
                            <Typography className="ticket-details-attachment-name">
                              {file.filename || "Attachment"}
                            </Typography>
                            <Typography className="ticket-details-attachment-meta">
                              {file.recorded_at_timezone || "Uploaded file"}
                            </Typography>
                          </div>
                        </div>
                        <div className="ticket-details-attachment-action">
                          <span className="ticket-details-link-btn">Open</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="ticket-details-card">
              <div className="ticket-details-card-head">
                <div className="ticket-details-section-title">
                  <span className="ticket-details-section-icon">
                    <Activity size={16} />
                  </span>
                  <Typography className="ticket-details-section-heading">
                    Audit Timeline
                  </Typography>
                </div>
              </div>

              {ticketData.length === 0 ? (
                <div className="ticket-details-timeline-empty">
                  <Activity size={36} color="#b8bfc9" />
                  <Typography className="ticket-details-empty-title">
                    No Activity Found
                  </Typography>
                  <Typography className="ticket-details-empty-copy">
                    This ticket exists, but no status transitions have been recorded yet.
                  </Typography>
                </div>
              ) : (
                <div className="ticket-details-timeline">
                  {ticketData.map((item, index) => {
                    const { date, time } = formatDateTime(item.recorded_at_ist);
                    const isLast = index === ticketData.length - 1;

                    return (
                      <div
                        key={`${item.recorded_at_ist || ""}-${index}`}
                        className={`ticket-details-timeline-item ${
                          isLast ? "ticket-details-timeline-item-last" : ""
                        }`}
                      >
                        <div className="ticket-details-timeline-rail">
                          <span
                            className={`ticket-details-timeline-dot ticket-details-timeline-dot-${getStatusVariant(
                              item.to_status,
                            )}`}
                          />
                          {!isLast && <span className="ticket-details-timeline-line" />}
                        </div>

                        <div className="ticket-details-timeline-card">
                          <div className="ticket-details-timeline-top">
                            <div className="ticket-details-timeline-statuses">
                              <span className="ticket-details-timeline-label">Status</span>
                              <span className="ticket-details-badge ticket-details-badge-outline">
                                {toTitleCase(item.from_status || "Unknown")}
                              </span>
                              <ArrowRight size={14} color="#98a2b3" />
                              <span
                                className={`ticket-details-badge ticket-details-badge-${getStatusVariant(
                                  item.to_status,
                                )}`}
                              >
                                {toTitleCase(item.to_status || "Unknown")}
                              </span>
                            </div>
                            <div className="ticket-details-timeline-date">
                              <span className="ticket-details-timeline-date-main">{date}</span>
                              <span className="ticket-details-timeline-date-sub">
                                <Clock size={12} />
                                {time} IST
                              </span>
                            </div>
                          </div>

                          <div className="ticket-details-timeline-user">
                            <Avatar className="ticket-details-avatar">
                              {getInitials(item.done_by)}
                            </Avatar>
                            <div>
                              <Typography className="ticket-details-timeline-user-name">
                                {item.done_by || "Unknown User"}
                              </Typography>
                              <span className="ticket-details-timeline-user-sub">
                                <User size={12} />
                                Activity owner
                              </span>
                            </div>
                          </div>

                          <div className="ticket-details-timeline-note">
                            <MessageSquare size={14} />
                            <div>
                              <span className="ticket-details-timeline-note-label">Note</span>
                              <Typography className="ticket-details-timeline-note-text">
                                {item.note || "No note provided."}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default TicketDetails;
